/**
 * wayai delete-history — delete conversation history on a hub
 *
 * Destructive and irreversible — use to reset test data during development.
 * Preview hubs allow deleting any user (defaults to the caller). Production
 * hubs only allow deleting your own end-user profile; pass --user-id with
 * your own platform user_id in that case.
 *
 * Usage:
 *   wayai delete-history                              # caller's full history on this hub
 *   wayai delete-history -y                           # skip confirmation
 *   wayai delete-history --user-id <id>               # target a specific user
 *   wayai delete-history --conversation-id <id>       # delete just one conversation
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';
import { confirm } from '../lib/utils.js';

function parseStringFlag(args: string[], flag: string): string | undefined {
  const idx = args.findIndex((a) => a === flag);
  if (idx !== -1) {
    const next = args[idx + 1];
    if (next && !next.startsWith('-')) return next;
  }
  const prefix = `${flag}=`;
  const inline = args.find((a) => a.startsWith(prefix));
  if (inline) return inline.slice(prefix.length);
  return undefined;
}

export async function deleteHistoryCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  const skipConfirm = args.includes('-y') || args.includes('--yes');
  const userIdFlag = parseStringFlag(args, '--user-id');
  const conversationId = parseStringFlag(args, '--conversation-id');

  // Resolve target user_id before prompting — if me() fails (401/5xx), surface
  // it before the user commits to a destructive confirmation.
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  const userId = userIdFlag ?? (await client.me()).user_id;

  if (!skipConfirm) {
    const confirmed = await confirm(
      'This will permanently delete conversation history on this hub. Continue?'
    );
    if (!confirmed) {
      console.log('Aborted.');
      return;
    }
  }

  const result = await (conversationId
    ? client.deleteHistory(hubId, { userId, mode: 'current', conversationId })
    : client.deleteHistory(hubId, { userId, mode: 'all' }));

  console.log(`Deleted ${result.deleted_count} conversation(s).`);
}
